import { useEffect, useRef, useState } from 'react'
import { differenceInSeconds } from 'date-fns'
import gql from 'graphql-tag'
import { v4 as uuid } from 'uuid'
import { LlmChatMessageSenderType } from '@/__gen/types'
import { apolloClient } from '@/state/apollo-client'
import { useFormContext } from '@/state/main-form/reducer'
import { FormLlmInterfacesQuery } from './__gen'
import { PollLlmChatQuery, usePollLlmChatLazyQuery, useSendMessageMutation } from './__gen/useLlm'

type Props = {
	formLlmInterface: NonNullable<FormLlmInterfacesQuery['formLlmInterface_List']>['objects'][number]
}

gql`
	mutation sendMessage(
		$id: ID!
		$content: String!
		$createChatForFormLlmInterface: ID!
		$chat: ID!
		$recordId: ID!
	) {
		llmChatMessage_Update(
			input: {
				id: $id
				content: $content
				chat: $chat
				record: $recordId
				createChatForFormLlmInterface: $createChatForFormLlmInterface
			}
		) {
			object {
				id
				chat {
					id
				}
			}
			errors {
				field
				messages
			}
		}
	}
`

const GET_MESSAGES_QUERY = gql`
	query PollLlmChat($chatId: ID!) {
		llmChat(id: $chatId) {
			id
			created
			messages(orderBy: [{ field: created, direction: ASC }]) {
				id
				content
				created
				sender
				responseReceivedAt
			}
		}
	}
`

const getBackoffMs = (start: Date) => {
	const diff = differenceInSeconds(new Date(), start)
	if (diff < 10) {
		return 1
	} else if (diff < 60) {
		return 3
	} else {
		return 10
	}
}

export const useLlm = ({ formLlmInterface }: Props) => {
	const [sendMessage, { loading: sendMessageLoading, data: sendMessageData }] = useSendMessageMutation()
	const [get, { data, loading: pollLoading }] = usePollLlmChatLazyQuery({})
	const formContext = useFormContext()
	const chatIdRef = useRef<string>(uuid())
	const [loading, setLoading] = useState(false)

	const send = async ({ content }: { content: string }) => {
		const id = uuid()
		apolloClient.writeQuery<PollLlmChatQuery>({
			query: GET_MESSAGES_QUERY,
			data: {
				__typename: 'PharosQuery',
				llmChat: {
					__typename: 'LlmChatType',
					id: chatIdRef.current,
					created: new Date().toISOString(),
					messages: [
						{
							__typename: 'LLmChatMessageType',
							id,
							content,
							created: new Date().toISOString(),
							responseReceivedAt: null,
							sender: LlmChatMessageSenderType.User,
						},
					],
				},
			},
		})
		setLoading(true)
		const resp = await sendMessage({
			variables: {
				id,
				chat: chatIdRef.current,
				content,
				createChatForFormLlmInterface: formLlmInterface.id,
				recordId: formContext.record.id,
			},
		})

		if (resp.errors || !resp.data?.llmChatMessage_Update?.object) {
			setLoading(false)
			return
		}
		await poll()
	}

	const checkCompletion = async () => {
		const resp = await get({ variables: { chatId: chatIdRef.current } })
		const chat = resp.data?.llmChat
		if (!chat) {
			return
		}
		const finished = chat.messages.reduce((p, c) => p && !!c.responseReceivedAt, true)
		return finished
	}

	const poll = async () => {
		const start = new Date()
		while (!(await checkCompletion())) {
			if (differenceInSeconds(new Date(), start) > 300) {
				console.warn('llm poll timed out')
				break
			}
			await new Promise<void>((resolve) =>
				setTimeout(() => {
					resolve()
				}, getBackoffMs(start)),
			)
		}
		setLoading(false)
	}

	return {
		send,
		loading,
		messages: data?.llmChat?.messages || null,
	}
}
